/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  IClaim,
  IClaimError,
  IClaimInputFile,
  IClaimsRemittance,
  IClaimStatusDetailsResponse,
} from 'src/app/shared/interfaces/claim/claim.interface';
import { ClaimsGQLService } from 'src/app/shared/services/claims/claims.graphql.service';

@Component({
  selector: 'claim-panel',
  templateUrl: './claim-panel.component.html',
})
export class ClaimPanelComponent implements OnChanges {
  @Input() claim: IClaim;
  @Input() panel?: ClaimPanelNames;

  claimPanelNames: ClaimPanelNames;

  isLoading = false;

  response: any;

  constructor(private claimsGQLService: ClaimsGQLService) {}

  ngOnChanges(changes: SimpleChanges): void {
    const panel = changes.panel.currentValue;
    if (panel) {
      this.onPanelContentSelect(panel);
    }
  }

  onPanelContentSelect(event) {
    this.isLoading = true;

    const functions: funcs = {
      statusDetails: this.getClaimStatusDetails(),
      keyfields: this.getKeyfields(),
      inputClaim: this.getClaimInput(),
      outputClaim: this.getClaimOutput(),
      errors: this.getClaimErrors(),
      inputFile: this.getClaimInputFile(),
      transmit: this.getClaimTransmitFile(),
      remittance: this.getClaimsRemittance(),
    };

    functions[event].subscribe({
      next: (response) => {
        this.isLoading = false;
        this.response = response;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private getKeyfields(): Observable<string> {
    const tt = '';
    return of(tt);
  }

  private getClaimInput(): Observable<string> {
    const { claimId } = this.claim;

    if (claimId) {
      return this.claimsGQLService.getClaimsInput(claimId).pipe(
        map((x) => {
          return x.text;
        }),
      );
    }

    throw new Error('Claim ID is missing');
  }

  private getClaimOutput() {
    const { claimId } = this.claim;

    if (claimId) {
      return this.claimsGQLService.getClaimsOutput(claimId).pipe(
        map((x) => {
          return x.inputData;
        }),
      );
    }

    throw new Error('Claim ID is missing');
  }

  private getClaimErrors(): Observable<IClaimError[]> {
    const { claimId } = this.claim;

    if (!claimId) {
      throw new Error('Claim ID is missing');
    }

    return this.claimsGQLService.getClaimsErrors({ claimId }).pipe(
      map((x) => {
        return x.nodes;
      }),
    );
  }

  private getClaimInputFile(): Observable<IClaimInputFile> {
    const { claimId } = this.claim;
    const groupId = this.claim.reportId;

    if (!claimId) {
      throw new Error('Claim ID is missing');
    }

    if (!groupId) {
      throw new Error('Group ID is missing');
    }

    return this.claimsGQLService.getClaimInputFile({
      claimId,
      x_GROUP_ID: groupId,
    });
  }

  private getClaimTransmitFile(): Observable<IClaimInputFile> {
    const { claimId } = this.claim;
    const groupId = this.claim.reportId;

    if (!claimId) {
      throw new Error('Claim ID is missing');
    }

    if (!groupId) {
      throw new Error('Group ID is missing');
    }

    return this.claimsGQLService.getClaimTransmitFile({
      claimId,
      x_GROUP_ID: groupId,
    });
  }

  private getClaimsRemittance(): Observable<IClaimsRemittance[]> {
    const { claimId } = this.claim;

    if (!claimId) {
      throw new Error('Claim ID is missing');
    }

    return this.claimsGQLService.getClaimsRemittance({ claimId }).pipe(
      map((x) => {
        return x.nodes;
      }),
    );
  }

  private getClaimStatusDetails(): Observable<IClaimStatusDetailsResponse[]> {
    const { claimId } = this.claim;

    if (!claimId) {
      throw new Error('Claim ID is missing');
    }

    return this.claimsGQLService.getClaimStatusDetails(claimId).pipe(
      map((x) => {
        return x.statusDetails;
      }),
    );
  }
}

export type funcs = Record<string, Observable<any>>;

export const ClaimPanelNamesOptions = [
  'Claim Errors',
  'Input Claim',
  'Input File',
  'Key Fields',
  'Output Claim',
  'Remittance',
  'Claim Details',
  'Transmit/Print File',
] as const;

export type ClaimPanelNames = (typeof ClaimPanelNamesOptions)[number];

export type IClaimsRemittanceDTO = {
  isCarrierExpanded: boolean;
  isPayerExpanded: boolean;
  isClaimExpanded: boolean;
  isPatientExpanded: boolean;
  isProviderExpanded: boolean;
  isFileExpanded: boolean;
} & IClaimsRemittance;
